import React, { useState } from 'react';
import { Document, Paragraph, Packer, TextRun, HeadingLevel, ExternalHyperlink, Footer } from 'docx';
import { saveAs } from 'file-saver';
import '../styles/ExportButton.css';
import exportIcon from '../assets/exportIcon.svg';

const ExportButton = ({ nodes, sessionId }) => {
  const [isExporting, setIsExporting] = useState(false);

  const formatContentForExport = (nodes) => {
    // Create a map of all nodes for easy lookup
    const nodeMap = new Map(nodes.map(node => [node.id, node]));
    const hierarchy = {};

    // Helper function to recursively build node hierarchy
    const buildNodeHierarchy = (nodeId) => {
      const node = nodeMap.get(nodeId);
      if (!node) return null;

      const hierarchyNode = {
        title: node.data.originalHeader,
        content: node.data.originalContent,
        children: {},
        sources: node.data.urlSources || [],
      };

      // Find all children of current node
      nodes.forEach((childNode) => {
        if (childNode.parentNode === nodeId) {
          hierarchyNode.children[childNode.id] = buildNodeHierarchy(childNode.id);
        }
      });

      return hierarchyNode;
    };

    // Process root nodes (nodes without parents)
    nodes.forEach((node) => {
      if (!node.parentNode) {
        hierarchy[node.id] = buildNodeHierarchy(node.id);
      }
    });

    return hierarchy;
  };

  const parseFormattedText = (text) => {
    const regex = /(\*\*\*([^\\*]+)\*\*\*)|(\*\*([^\\*]+)\*\*)|(\*([^\\*]+)\*)/g;
    const segments = [];
    let lastIndex = 0;

    text.replace(regex, (match, boldItalic, biText, bold, bText, italic, iText, offset) => {
      if (offset > lastIndex) {
        segments.push(new TextRun({ 
          text: text.slice(lastIndex, offset),
          font: "Calibri"
        }));
      }
      if (biText) {
        segments.push(new TextRun({ 
          text: biText, 
          bold: true, 
          italics: true,
          font: "Calibri"
        }));
      } else if (bText) {
        segments.push(new TextRun({ 
          text: bText, 
          bold: true,
          font: "Calibri"
        }));
      } else if (iText) {
        segments.push(new TextRun({ 
          text: iText, 
          italics: true,
          font: "Calibri"
        }));
      }
      lastIndex = offset + match.length;
    });

    if (lastIndex < text.length) {
      segments.push(new TextRun({ 
        text: text.slice(lastIndex),
        font: "Calibri"
      }));
    }

    return segments;
  };

  const createDocxDocument = (hierarchy) => {
    const children = [];

    // Title page
    children.push(
      new Paragraph({
        children: [new TextRun({ 
          text: 'DeepNode Knowledge Map Export', 
          bold: true, 
          size: 40,
          font: "Calibri"
        })],
        heading: HeadingLevel.TITLE,
        spacing: { after: 100 },
      }),
      new Paragraph({
        children: [new TextRun({ 
          text: `Generated: ${new Date().toLocaleString()}`,
          font: "Calibri",
          italics: true,
          color: "808080",
        })],
        spacing: { after: 100 },
      }),
      new Paragraph({
        children: [new TextRun({ 
          text: `Note: The content is AI-driven and could be inaccurate. Please verify the information before using it.`,
          font: "Calibri",
          italics: true,
          color: "808080",
        })],
        spacing: { after: 200 },
      }),
      new Paragraph({
        children: [new TextRun({ break: 1 })],
      })
    );

    const processNode = (node, level = 1) => {
      const nodeChildren = [];

      // Section header
      nodeChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: node.title,
              bold: true,
              color: level === 1 ? '2E74B5' : '000000',
              font: "Calibri"
            }),
          ],
          heading: HeadingLevel[`HEADING_${Math.min(level, 6)}`],
          spacing: { before: 400, after: 200 },
        })
      );

      // Content
      if (node.content) {
        const paragraphs = node.content.split('\n').filter((p) => p.trim());
        paragraphs.forEach((para) => {
          nodeChildren.push(
            new Paragraph({
              children: parseFormattedText(para),
              spacing: { after: 200 },
            })
          );
        });
      }

      // Sources
      if (node.sources?.length > 0) {
        nodeChildren.push(
          new Paragraph({
            children: [new TextRun({ 
              text: 'Suggested reads', 
              bold: true,
              font: "Calibri"
            })],
            spacing: { before: 400, after: 200 },
          })
        );

        node.sources.forEach((source) => {
          nodeChildren.push(
            new Paragraph({
              children: [
                new TextRun({ 
                  text: '• ',
                  font: "Calibri"
                }),
                new ExternalHyperlink({
                  children: [
                    new TextRun({
                      text: source.title || source.link,
                      style: 'Hyperlink',
                      font: "Calibri"
                    }),
                  ],
                  link: source.link,
                }),
              ],
              indent: { left: 720 },
              spacing: { after: 200 },
            })
          );
        });
      }

      if (node.children) {
        Object.values(node.children).forEach((child) => {
          nodeChildren.push(...processNode(child, level + 1));
        });
      }

      return nodeChildren;
    };

    Object.values(hierarchy).forEach((node) => {
      children.push(...processNode(node));
    });

    // Create footer with right-aligned text
    const footer = new Footer({
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: "Made with DeepNode.in",
                font: "Calibri",
                size: 20,
                color: "808080"
              }),
            ],
            alignment: "right",  // Right-align the footer text
            spacing: { before: 200 }
          }),
        ],
      });  

    return new Document({
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 1440,
                right: 1440,
                bottom: 1440,
                left: 1440,
              },
            },
          },
          children: children,
          footers: {
            default: footer,  // Apply footer to all pages
          },
        },
      ],
      styles: {
        default: {
          document: {
            run: {
              font: "Calibri",
            }
          },
          heading1: {
            run: {
              size: 36,
              bold: true,
              color: '2E74B5',
              font: "Calibri"
            },
            paragraph: {
              spacing: { before: 400, after: 200 },
            },
          },
          heading2: {
            run: {
              size: 32,
              bold: true,
              font: "Calibri"
            },
            paragraph: {
              spacing: { before: 400, after: 200 },
            },
          },
        },
      },
    });
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const hierarchy = formatContentForExport(nodes);
      const doc = createDocxDocument(hierarchy);
      
      const topicNode = nodes.find(node => node.data.originalHeader === 'topic name');
      const fileName = topicNode?.data.originalContent || 'untitled';
      
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${fileName}.docx`);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };
  
  
  return (
    <button
      onClick={handleExport}
      className={`export-button ${isExporting ? 'exporting' : ''}`}
      disabled={isExporting}
      title="Export"
    >
      <img
        src={exportIcon}
        alt="Export Icon"
        className="export-icon"
        style={{
          width: '14px',
          height: '14px',
        }}
      />
      {/* {!isExporting && <span className="export-tooltip">Export</span>} */}
      {/* {isExporting && <span className="export-tooltip">Exporting...</span>} */}
    </button>
  );
};

export default ExportButton;